import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { HotToastService } from '@ngxpert/hot-toast';
import { catchError, throwError } from 'rxjs';

import { IdentityClientService } from '@identity/identity-client.service';
import { IdentityCommonService } from '@identity/identity-common.service';
import { IdentityConstants } from '@identity/identity-constants.service';
import { ErrorService } from '@shared/services/error.service';

const enum HttpStatusCode {
    BadRequest = 400,
    Unauthorized = 401,
    Forbidden = 403,
    NotFound = 404,
    UnsupportedMediaType = 415,
    SessionExpired = 419,
    InternalServerError = 500,
    ServiceUnavailable = 503
};

const ERROR_ROUTE = 'error';
const SIGN_IN_ROUTE = 'signIn';

export const errorInterceptor: HttpInterceptorFn = (req, next) => {
    const router = inject(Router);
    const notification = inject(HotToastService);
    const client = new IdentityClientService(new IdentityConstants()).get();
    const errorService = inject(ErrorService);
    const identityCommonService = inject(IdentityCommonService);

    return next(req).pipe(
        catchError((error: HttpErrorResponse) => {
            const errorMessage = getErrorMessage(error.status);
            console.error(`HTTP error: ${errorMessage}`);

            if (shouldNavigateToErrorPage(error.status)) {
                errorService.setHasError(true);

                router.navigate([`${client.url}/${ERROR_ROUTE}`]);
            }

            if (error.status === HttpStatusCode.SessionExpired) {
                notification.error(errorMessage);
            }

            if (error.status === HttpStatusCode.Unauthorized) {
                const isUserLogged = identityCommonService.isLogged();

                if (isUserLogged)
                    identityCommonService.logout();

                router.navigate([`${client.url}/${SIGN_IN_ROUTE}`]);
            }

            return throwError(() => error);
        })
    );
};

function getErrorMessage(status: number): string {
    switch (status) {
        case HttpStatusCode.BadRequest:
            return 'Bad Request';
        case HttpStatusCode.Unauthorized:
            return 'Unauthorized';
        case HttpStatusCode.Forbidden:
            return 'Forbidden';
        case HttpStatusCode.NotFound:
            return 'Not found';
        case HttpStatusCode.UnsupportedMediaType:
            return 'Unsupported media type';
        case HttpStatusCode.SessionExpired:
            return 'Session expired';
        case HttpStatusCode.InternalServerError:
            return 'Internal server error';
        case HttpStatusCode.ServiceUnavailable:
            return 'Service unavailable';
        default:
            return 'An unknown error occurred';
    }
}

function shouldNavigateToErrorPage(status: number): boolean {
    return status === HttpStatusCode.InternalServerError || status === HttpStatusCode.ServiceUnavailable;
}